<script setup>
import {onMounted, onUnmounted, ref} from "vue";
import axios from "axios";
import { format } from "date-fns";
import store from "@/store/store";


const is_manager = ref(false)
const is_engineer = ref(false)
const requests = ref([]);

function fetchRequests() {
  axios.get("request_from_landing/")
      .then((response) => {
        requests.value = response.data.results.map((request) => ({
          id: request.id,
          name: request.name,
          phone: request.phone,
          is_resolved: request.is_resolved,
          is_faulty: request.is_faulty,
          created_datetime: request.created_datetime,
          closed_datetime: request.closed_datetime,
          is_complete: false,
        }));

        // Update localStorage and dispatch event
        const new_count = response.data.newCount;
        localStorage.setItem('requests_from_landing', new_count);
        window.dispatchEvent(new CustomEvent('landingRequestsUpdated'));
      })
      .catch((error) => {
        console.error(error);
      });
}

function completeRequest(item) {
  axios.patch("request_from_landing/", {
    request_id: item.id,
    is_faulty: item.is_faulty,
  })
      .then(() => {
        fetchRequests(); // Refresh data after update
      })
      .catch((error) => {
        console.error(error);
      });
}

function formatPhoneNumber(phone) {
  return phone.replace(/(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})/, '+$1 ($2) $3-$4-$5');
}

function formattedTimestamp(datetime) {
  if (!datetime) return '';
  try {
    const date = new Date(datetime);
    if (isNaN(date.getTime())) return '';
    return format(date, 'dd/MM/yyyy, HH:mm');
  } catch {
    return '';
  }
}

function handleStatusChange(item) {
  if (item.is_faulty) item.is_complete = false;
  if (item.is_complete) item.is_faulty = false;
}

function getButtonText(item) {
  if (item.is_complete || item.is_faulty) return 'Подтвердить';
  return 'Выберите статус';
}
onMounted(() => {
  fetchRequests();
  is_manager.value = store.getters['profile/is_manager']
  is_engineer.value = store.getters['profile/is_engineer']
});

</script>

<template>
  <div class="content-wrapper">
    <div class="container-fluid py-5 mt-4">

      <!-- Unresolved Section -->
      <h4 class="fw-bold mb-4">Новые заявки</h4>
      <div class="row g-4 row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4">
        <div
            class="col"
            v-for="item in requests.filter(r => !r.is_resolved)"
            :key="item.id"
        >
          <div
              class="card h-100 border-0 shadow rounded-4"

          >
            <div class="card-body d-flex flex-column p-4">
              <!-- Client Info -->
              <div class="mb-3">
                <h5 class="fw-bold">{{ item.name }}</h5>
                <p class="mb-2">
                  <a
                      :href="`tel:${item.phone}`"
                      class="text-decoration-none"
                  >
                    <i class="fas fa-phone-alt me-2"></i>{{ formatPhoneNumber(item.phone) }}
                  </a>
                </p>
                <small class="">
                  <i class="far fa-clock me-1"></i>{{ formattedTimestamp(item.created_datetime) }}
                </small>
              </div>

              <!-- Toggles -->
              <div class="mb-4" v-if="is_manager">
                <div class="form-check form-switch mb-2">
                  <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="item.is_complete"
                      :disabled="item.is_faulty"
                      @change="handleStatusChange(item)"
                  />
                  <label
                      class="form-check-label"
                      :class="{ 'text-success fw-semibold': item.is_complete }"
                  >
                    Завершена
                  </label>
                </div>

                <div class="form-check form-switch">
                  <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="item.is_faulty"
                      :disabled="item.is_complete"
                      @change="handleStatusChange(item)"
                  />
                  <label
                      class="form-check-label"
                      :class="{ 'text-danger fw-semibold': item.is_faulty }"
                  >
                    Проблемная
                  </label>
                </div>
              </div>

              <!-- Button -->
              <div class="mt-auto" v-if="is_manager">
                <button
                    class="btn btn-sm w-100 fw-semibold"
                    :class="{
                    'btn-success': item.is_complete,
                    'btn-danger': item.is_faulty,
                    'btn-outline-secondary': !item.is_complete && !item.is_faulty
                  }"
                    @click="completeRequest(item)"
                    :disabled="!item.is_complete && !item.is_faulty"
                >
                  {{ getButtonText(item) }}
                </button>
              </div>
              <div v-else>
                <p class="text-warning">Не завершена</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Resolved Section -->
      <h4 class="fw-bold mt-5 mb-4">Завершённые заявки</h4>
      <div class="row g-4 row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4">
        <div
            class="col"
            v-for="item in requests.filter(r => r.is_resolved)"
            :key="item.id"
        >
          <div
              class="card h-100 border-0 shadow rounded-4"

          >
            <div class="card-body d-flex flex-column p-4">
              <!-- Client Info -->
              <div class="mb-3">
                <h5 class="fw-bold">{{ item.name }}</h5>
                <p class="mb-2">
                  <a
                      :href="`tel:${item.phone}`"
                      class="text-decoration-none"
                  >
                    <i class="fas fa-phone-alt me-2"></i>{{ formatPhoneNumber(item.phone) }}
                  </a>
                </p>
                <small class="">
                  <i class="far fa-clock me-1"></i>{{ formattedTimestamp(item.created_datetime) }}
                </small>
              </div>

              <!-- No toggles or button for resolved -->
              <div class="mt-auto">
                <span v-if="!item.is_faulty" class="badge bg-white text-dark fw-semibold w-100 py-2">Завершена</span>
                <span v-else class="badge bg-white text-dark fw-semibold w-100 py-2">Проблемная завершена</span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>


<style scoped>
.card {
  transition: transform 0.2s ease-in-out;
}
.card:hover {
  transform: translateY(-4px);
}
</style>
